import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders, getUser } from '../utils/auth';
import Sidebar from '../components/Sidebar';
import { FileText, Clock, CheckCircle, XCircle, PlusCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [stats, setStats] = useState({
    my_requests: 0,
    pending_approval: 0,
    approved: 0,
    rejected: 0,
    closed: 0
  });
  const [recentPTWs, setRecentPTWs] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [allApproverPTWs, setAllApproverPTWs] = useState([]);  // New: All PTWs for approver
  const [loading, setLoading] = useState(true);

  const isVendor = user.roles?.includes('vendor');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch stats
      const statsRes = await axios.get(`${API}/ptw/stats/dashboard`, { headers: getAuthHeaders() });
      setStats(statsRes.data);

      // EVALUASI 3: Conditional data fetching based on role
      if (isVendor) {
        // Vendor: fetch their own PTWs
        const ptwRes = await axios.get(`${API}/ptw/my-requests`, { headers: getAuthHeaders() });
        setRecentPTWs(ptwRes.data.slice(0, 5)); // Latest 5
      } else {
        // Approver: fetch pending approvals AND all PTWs they're involved in
        const [pendingRes, allRes] = await Promise.all([
          axios.get(`${API}/ptw/pending-approval`, { headers: getAuthHeaders() }),
          axios.get(`${API}/ptw/all-approver`, { headers: getAuthHeaders() })
        ]);
        setPendingApprovals(pendingRes.data);
        setAllApproverPTWs(allRes.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Gagal memuat data dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', label: 'Draft' },
      waiting_high_risk_forms: { color: 'bg-yellow-100 text-yellow-800', label: 'Waiting High Risk Forms' },
      waiting_pic: { color: 'bg-blue-100 text-blue-800', label: 'Waiting PIC' },
      waiting_area_manager: { color: 'bg-blue-100 text-blue-800', label: 'Waiting Area Manager' },
      waiting_she: { color: 'bg-blue-100 text-blue-800', label: 'Waiting SHE' },
      waiting_management: { color: 'bg-blue-100 text-blue-800', label: 'Waiting Leadership Team' },
      waiting_closing: { color: 'bg-purple-100 text-purple-800', label: 'Waiting Closing' },
      waiting_closing_approval: { color: 'bg-purple-100 text-purple-800', label: 'Waiting Closing Approval' },
      needs_revision: { color: 'bg-orange-100 text-orange-800', label: 'Needs Revision' },
      approved: { color: 'bg-green-100 text-green-800', label: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' },
      closed: { color: 'bg-gray-100 text-gray-800', label: 'Closed' }
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 lg:ml-64 flex items-center justify-center pt-16 lg:pt-0">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 lg:ml-64 overflow-auto pt-16 lg:pt-0">
        <div className="max-w-7xl mx-auto p-6">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
            <p className="text-gray-600 mt-1">
              Selamat datang, <span className="font-semibold">{user.name}</span>
            </p>
          </div>

          {/* Stats Cards - EVALUASI 3: Different for Vendor vs Approver */}
          {isVendor ? (
            // VENDOR STATS
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Requests</p>
                    <p className="text-3xl font-bold text-gray-800 mt-2">{stats.my_requests}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <FileText className="text-blue-600" size={24} />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Pending Action</p>
                    <p className="text-3xl font-bold text-yellow-600 mt-2">{stats.pending_approval}</p>
                  </div>
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Clock className="text-yellow-600" size={24} />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Approved</p>
                    <p className="text-3xl font-bold text-green-600 mt-2">{stats.approved}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="text-green-600" size={24} />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Rejected</p>
                    <p className="text-3xl font-bold text-red-600 mt-2">{stats.rejected}</p>
                  </div>
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <XCircle className="text-red-600" size={24} />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // APPROVER STATS - EVALUASI 3: Only Pending Approval
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow p-6 col-span-1 md:col-span-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Pending Approvals</p>
                    <p className="text-4xl font-bold text-blue-600 mt-2">{stats.pending_approval}</p>
                    <p className="text-sm text-gray-500 mt-2">PTW menunggu approval dari Anda</p>
                  </div>
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                    <AlertTriangle className="text-blue-600" size={32} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions - Only for Vendor */}
          {isVendor && (
            <div className="mb-8">
              <button
                onClick={() => navigate('/ptw/new')}
                className="w-full md:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 font-semibold"
              >
                <PlusCircle size={20} />
                Buat PTW Baru
              </button>
            </div>
          )}

          {/* Content Area - EVALUASI 3: Different for Vendor vs Approver */}
          {isVendor ? (
            // VENDOR VIEW: Recent PTW Requests
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-800">Recent PTW Requests</h2>
                  <button
                    onClick={() => navigate('/my-requests')}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Lihat Semua →
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                {recentPTWs.length > 0 ? (
                  <div className="space-y-4">
                    {recentPTWs.map((ptw) => (
                      <div
                        key={ptw.id}
                        onClick={() => navigate(`/ptw/${ptw.id}`)}
                        className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-gray-800">{ptw.lokasi_pekerjaan}</h3>
                          {getStatusBadge(ptw.status)}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{ptw.deskripsi_pekerjaan}</p>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>ID: {ptw.id.substring(0, 8)}</span>
                          <span>Tanggal: {ptw.tanggal_mulai}</span>
                        </div>
                        {ptw.high_risk_activities && ptw.high_risk_activities.length > 0 && (
                          <div className="mt-2 flex gap-2 flex-wrap">
                            {ptw.high_risk_activities.map((activity, idx) => (
                              <span key={idx} className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs">
                                ⚠️ {activity}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="mx-auto text-gray-400 mb-4" size={48} />
                    <p className="text-gray-600">Belum ada PTW request</p>
                    <button
                      onClick={() => navigate('/ptw/new')}
                      className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Buat PTW Pertama
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // APPROVER VIEW: Pending Approvals AND All PTWs
            <div className="space-y-6">
              {/* Pending Approvals Section */}
              <div className="bg-white rounded-lg shadow">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-800">My Pending Approvals</h2>
                </div>
                
                <div className="p-6">
                  {pendingApprovals.length > 0 ? (
                    <div className="space-y-4">
                      {pendingApprovals.map((ptw) => (
                        <div
                          key={ptw.id}
                          onClick={() => navigate(`/ptw/${ptw.id}`)}
                          className="p-4 border border-blue-200 rounded-lg hover:bg-blue-50 cursor-pointer transition bg-blue-25"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-semibold text-gray-800">{ptw.lokasi_pekerjaan}</h3>
                            {getStatusBadge(ptw.status)}
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{ptw.deskripsi_pekerjaan}</p>
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                            <span>Requester: {ptw.requester_name}</span>
                            <span>Tanggal: {ptw.tanggal_mulai}</span>
                          </div>
                          {ptw.high_risk_activities && ptw.high_risk_activities.length > 0 && (
                            <div className="mt-2 flex gap-2 flex-wrap">
                              {ptw.high_risk_activities.map((activity, idx) => (
                                <span key={idx} className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs">
                                  ⚠️ {activity}
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="mt-3 pt-3 border-t border-blue-200">
                            <p className="text-xs text-blue-600 font-medium">
                              ✅ Menunggu approval dari Anda sebagai {ptw.current_approver_role?.replace(/_/g, ' ').toUpperCase()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CheckCircle className="mx-auto text-gray-400 mb-4" size={36} />
                      <p className="text-gray-600">Tidak ada PTW yang menunggu approval</p>
                    </div>
                  )}
                </div>
              </div>

              {/* All PTWs Section - where user is approver */}
              <div className="bg-white rounded-lg shadow">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-800">Semua PTW Saya</h2>
                  <p className="text-sm text-gray-500 mt-1">Semua PTW di mana Anda sebagai PIC/Approver</p>
                </div>
                
                <div className="p-6">
                  {allApproverPTWs.length > 0 ? (
                    <div className="space-y-4">
                      {allApproverPTWs.map((ptw) => (
                        <div
                          key={ptw.id}
                          onClick={() => navigate(`/ptw/${ptw.id}`)}
                          className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-semibold text-gray-800">{ptw.lokasi_pekerjaan}</h3>
                            {getStatusBadge(ptw.status)}
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{ptw.deskripsi_pekerjaan}</p>
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                            <span>Requester: {ptw.requester_name}</span>
                            <span>Tanggal: {ptw.tanggal_mulai}</span>
                          </div>
                          {ptw.high_risk_activities && ptw.high_risk_activities.length > 0 && (
                            <div className="mt-2 flex gap-2 flex-wrap">
                              {ptw.high_risk_activities.map((activity, idx) => (
                                <span key={idx} className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs">
                                  ⚠️ {activity}
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="mt-3 pt-3 border-t border-gray-200 flex gap-2 flex-wrap">
                            {ptw.pic_id === user.id && (
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">PIC</span>
                            )}
                            {ptw.area_manager_id === user.id && (
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">Area Manager</span>
                            )}
                            {ptw.she_id === user.id && (
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs">SHE</span>
                            )}
                            {ptw.management_id === user.id && (
                              <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">Leadership Team</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FileText className="mx-auto text-gray-400 mb-4" size={36} />
                      <p className="text-gray-600">Belum ada PTW</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Help Section */}
          <div className="mt-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">💡 Quick Tips</h3>
            {isVendor ? (
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Pastikan semua field wajib terisi sebelum submit PTW</li>
                <li>• Jika memilih high risk activities, Anda akan diminta mengisi form detail</li>
                <li>• PTW yang perlu revisi dapat diedit dari halaman detail</li>
                <li>• Download PDF untuk dokumentasi offline</li>
              </ul>
            ) : (
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Review semua detail PTW sebelum approve</li>
                <li>• Gunakan fitur "Revise" untuk meminta perubahan ke vendor</li>
                <li>• PIC dapat memilih approver selanjutnya setelah mereview PTW</li>
                <li>• High risk activities memerlukan perhatian khusus</li>
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
